import React from 'react';
import { Badge } from '@/components/ui/badge';
import { User, Briefcase, Package } from 'lucide-react';
import type { EmployeeType } from '@/types/timesheet';
import { cn } from '@/lib/utils';

interface EmployeeTypeBadgeProps {
  type?: EmployeeType;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

export const EmployeeTypeBadge: React.FC<EmployeeTypeBadgeProps> = ({
  type,
  size = 'md',
  showIcon = true,
  className,
}) => {
  if (!type) return null;

  const getTypeConfig = (type: EmployeeType) => {
    switch (type) {
      case 'Person':
        return {
          icon: User,
          label: 'Person',
          className: 'bg-info-100 text-info-700 border-info-200 dark:bg-info-900/30 dark:text-info-300 dark:border-info-800',
        };
      case 'Contractor':
        return {
          icon: Briefcase,
          label: 'Contractor',
          className: 'bg-warning-100 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-300 dark:border-warning-800',
        };
      case 'Asset':
        return {
          icon: Package,
          label: 'Asset',
          className: 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700',
        };
      default:
        return {
          icon: User,
          label: type,
          className: 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700',
        };
    }
  };

  const config = getTypeConfig(type);
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-xs h-5',
    md: 'text-xs h-6',
    lg: 'text-sm h-7',
  };

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-medium',
        sizeClasses[size],
        config.className,
        className
      )}
    >
      {showIcon && <Icon className={cn(iconSizeClasses[size], 'mr-1')} />}
      {config.label}
    </Badge>
  );
};
